import Cookie from 'js-cookie';
import { AuthConsumer } from '@/types/oauth';
import { FetchError, Get, Post, Put } from './helpers';
import { PresenterAuthType } from '@/types/user';
import { ChannelOauthIntegration, ChannelSSOIntegration } from '@/types/channel';
import { getSignature } from './utils';
import { PageRequest } from '@/route-handlers/page-request';
import getRequestBodyTheme from '@/themes/utils/get-request-body-theme';
import { ThemeName } from '@/types/theme';
import { getEnv } from '@/utils/env';

export const RefreshToken = async (): Promise<{ id_token: string }> => {
  return Get<{ id_token: string }>({
    url: `/authenticate/refresh-token`,
    includeCredentials: true,
  });
};

export class LoginError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LoginError';
    this.message = message;
  }
}

export const authLogin = async (
  email: string,
  password: string,
  two_factor_stored: string | null,
  authConsumer: AuthConsumer,
  allowNoChannels = false,
  theme?: ThemeName
): Promise<{
  token?: string;
  auth_consumer?: AuthConsumer;
  two_factor_required?: boolean;
  temp_profile?: string;
  refreshToken?: string;
  accessToken?: string;
  code?: string;
  idToken?: string;
  cors?: boolean;
}> => {
  try {
    const passwordResponse = await Post<{
      token?: string;
      auth_consumer?: AuthConsumer;
      two_factor_required?: boolean;
      temp_profile?: string;
    }>({
      url: '/v3/admin/auth/sign-in',
      includeCredentials: true,
      body: {
        email,
        password,
        two_factor_stored: two_factor_stored ? two_factor_stored : '',
        auth_consumer: authConsumer,
        allow_no_channels: allowNoChannels,
        theme: getRequestBodyTheme(theme),
      },
    });
    return passwordResponse;
  } catch (e) {
    if (e instanceof FetchError) {
      if (e.body?.error === 'needs_verification') {
        throw new LoginError('Email not verified, please contact support.');
      }

      if (e.body?.error === 'reset_required') {
        throw new LoginError('Password expired, please reset your password.');
      }

      if (e.body?.error === 'no_channels') {
        throw new LoginError('No channel access, please contact support.');
      }

      if (e.body?.error === 'Domain locked to SSO-only') {
        throw new LoginError('Please use SSO to sign in.');
      }

      if (e.body?.error?.includes('email_temporarily_locked')) {
        throw new LoginError('Account locked, please try again later.');
      }

      if (e.response.status === 403) {
        throw new LoginError('Incorrect email or password.');
      }

      if (e.response.status === 500) {
        throw new LoginError('Unknown error, please try again in a few minutes.');
      }
    }

    // Unknown error
    console.error('Error signing in');
    console.error(e);

    throw new LoginError('Unknown signin error');
  }
};

export function requestEmailAuthCode(email: string): Promise<{ temp_profile: string }> {
  const params = new URLSearchParams({ email });
  return Get<{ temp_profile: string }>({ url: `/v3/admin/auth/auth-code?${params.toString()}` });
}

export function GetAuthTypeForPresenter(email: string, channel?: number): Promise<PresenterAuthType> {
  const route = channel ? `/v3/auth/service/auth-type/${email}/${channel}` : `/v3/auth/service/auth-type/${email}`;
  return Get({ url: route });
}

export type GetLoginMethodResponse = {
  method: 'sso' | 'password';
  config?: ChannelSSOIntegration | ChannelOauthIntegration;
};

export async function GetLoginMethod(email: string, gr_meeting_id?: string): Promise<GetLoginMethodResponse> {
  const [hmac, key] = await getSignature();
  const params = new URLSearchParams({
    email,
  });
  if (gr_meeting_id) {
    params.append('gr_meeting_id', gr_meeting_id);
  }
  return Get({
    url: `/v3/admin/auth/login-method?${params.toString()}`,
    headers: {
      'bl-sig': hmac,
      'bl-key': key,
    },
  });
}

export function GetAccessTokenFromCode(
  code: string
): Promise<{ id_token: string; refresh_token: string; access_token?: string }> {
  return Get<{ id_token: string; refresh_token: string; access_token?: string }>({
    url: `/authenticate/access-token?code=${code}`,
  });
}

export function GetAccessTokenFromRefreshToken(
  refresh_token: string
): Promise<{ id_token: string; access_token?: string }> {
  return Get<{ id_token: string; access_token?: string }>({
    url: `/authenticate/refresh-token?refresh_token=${refresh_token}`,
  });
}

export function ExchangeLegacyToken(token: string): Promise<{ id_token: string; code?: string }> {
  return Get<{ id_token: string }>({
    url: `/authenticate/exchange-legacy`,
    headers: {
      'BL-TOKEN': token,
    },
    includeCredentials: true,
  });
}

export function LoginWithMagicLink(secret: string): Promise<{
  code: string;
  redirect_to?: string;
  from_app: string; // 'Greenroom' | 'Brandlive Platform' | 'BrandTV'
  idToken?: string;
  refreshToken?: string;
  accessToken?: string;
  resetToken?: string;
}> {
  return Post({
    url: `/v4/admin/auth/magic-link/${secret}`,
    includeCredentials: true,
  });
}

export function RequestMagicLink(
  email: string,
  from_app: string | null,
  redirect_to?: string,
  theme?: ThemeName
): Promise<{ message: string }> {
  return Post({
    url: '/v4/admin/auth/magic-link',
    body: {
      email,
      redirect_to,
      from_app,
      theme: getRequestBodyTheme(theme),
    },
  });
}

export function Logout(): Promise<void> {
  return Put({
    url: '/v4/admin/access-token/logout',
    includeCredentials: true,
  });
}

export async function CheckCookie(): Promise<void> {
  const url = new URL(window.location.href);
  const refreshToken = Cookie.get('refreshToken');
  const params = PageRequest.fromSearchParams(url.searchParams);

  if (refreshToken) {
    try {
      // Should throw if refresh token is invalid
      await RefreshToken();

      const checkCookieUrl = new URL(`${getEnv().VITE_API_HOST}/v4/admin/auth/checkCookie`);
      checkCookieUrl.search = params.toParams();

      window.location.href = checkCookieUrl.href;
    } catch (e) {
      console.error(e);
    }
  }
}
